import { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: string | string[];
}

export default function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { user, userRole, loading, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Fehler beim Abmelden:', error);
    }
  };

  // Show loading spinner during authentication OR when role is being fetched
  if (loading || (user && requiredRole && userRole === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Lade Authentifizierung...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (requiredRole && !hasRequiredRole(userRole, requiredRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <h2 className="text-2xl font-bold text-destructive mb-4">Zugriff verweigert</h2>
            <p className="text-muted-foreground mb-4">
              Sie haben nicht die erforderlichen Berechtigungen für diese Seite.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Erforderliche Rolle: {Array.isArray(requiredRole) ? requiredRole.join(' oder ') : requiredRole} | Ihre Rolle: {userRole}
            </p>
            <Button 
              onClick={handleSignOut}
              className="w-full"
            >
              Mit anderem Konto anmelden
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}

const hasRequiredRole = (userRole: string | null, requiredRole: string | string[]): boolean => {
  if (!userRole) return false;
  
  if (Array.isArray(requiredRole)) {
    return requiredRole.includes(userRole);
  }
  
  return userRole === requiredRole;
};